import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  TextField,
  Table,
  Modal,
  TableBody,
  TableCell,
  Box,
  styled,
  Typography,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  IconButton,
  useTheme,
} from '@mui/material';

import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
  Close as CloseIcon,
} from '@mui/icons-material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const IdCard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddIdCardModal, setShowAddIdCardModal] = useState(false);
  const [showEditIdCardModal, setShowEditIdCardModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalType, setSuccessModalType] = useState('');
  const [selectedIdCard, setSelectedIdCard] = useState(null);

  const staticIdCardData = [
    { id: 1, studentName: 'John Doe', issueDate: '2023-01-01', expiryDate: '2025-01-01' },
    { id: 2, studentName: 'Jane Smith', issueDate: '2023-02-01', expiryDate: '2025-02-01' },
    { id: 3, studentName: 'Alice Johnson', issueDate: '2023-03-01', expiryDate: '2025-03-01' },
    { id: 4, studentName: 'Bob Brown', issueDate: '2023-04-01', expiryDate: '2025-04-01' },
    { id: 5, studentName: 'Charlie Davis', issueDate: '2023-05-01', expiryDate: '2025-05-01' },
    { id: 6, studentName: 'Diana Evans', issueDate: '2023-06-01', expiryDate: '2025-06-01' },
  ];

  const [idCards, setIdCards] = useState(staticIdCardData);
  const [filteredData, setFilteredData] = useState(staticIdCardData);

  const handleAddIdCard = () => {
    setShowAddIdCardModal(true);
  };

  const handleEdit = (idCard) => {
    setSelectedIdCard(idCard);
    setShowEditIdCardModal(true);
  };

  const handleDelete = (idCard) => {
    setSelectedIdCard(idCard);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedIdCard(null);
  };

  const handleConfirmDelete = () => {
    setIdCards((prevIdCards) =>
      prevIdCards.filter((card) => card.id !== selectedIdCard.id)
    );
    setSnackbarMessage(`ID Card for ${selectedIdCard.studentName} deleted successfully!`);
    setOpenSnackbar(true);
  setSnackbarSeverity("error");
    setShowDeleteModal(false);
    setSelectedIdCard(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSaveIdCard = () => {
    setShowAddIdCardModal(false);
    setSuccessModalType('add');
    setSnackbarMessage('Added successfully!');  
  setOpenSnackbar(true);  
  setSnackbarSeverity("success");
  };

  const handleUpdateIdCard = () => {
    setShowEditIdCardModal(false);
    setSuccessModalType('edit');
    setSnackbarMessage('Updated successfully!');  
  setOpenSnackbar(true);  
  setSnackbarSeverity("success");
  };

  useEffect(() => {
    setFilteredData(
      idCards.filter(
        (idCard) =>
          idCard.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          idCard.issueDate.toLowerCase().includes(searchTerm.toLowerCase()) ||
          idCard.expiryDate.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, idCards]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container maxWidth={false} sx={{ width: '100%' }}>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center',gap:2 }}>
        <TextField
                           variant="outlined"
                           placeholder="Search..."
                           value={searchTerm}
                           onChange={(e) => setSearchTerm(e.target.value)}
                           InputProps={{
                             startAdornment: (
                               <InputAdornment position="start">
                                 <SearchIcon
                                   sx={{
                                     color: theme.palette.primary.main,
                                     marginLeft: 1.5,
                                     fontSize: 24,
                                   }}
                                 />
                               </InputAdornment>
                             ),
                             endAdornment: searchTerm && (
                               <InputAdornment position="end">
                                 <IconButton
                                   size="small"
                                   onClick={() => setSearchTerm('')}
                                   sx={{
                                     color: "grey",
                                     marginRight: 1,
                                   }}
                                 >
                                   <CloseIcon fontSize="small" />
                                 </IconButton>
                               </InputAdornment>
                             ),
                             sx: {
                               borderRadius: '30px',
                               backgroundColor: theme.palette.mode === 'dark'
                                 ? 'rgba(255, 255, 255, 0.15)'
                                 : 'rgba(255, 255, 255, 1)',
                               boxShadow: theme.palette.mode === 'dark'
                                 ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                                 : '0 2px 8px rgba(0, 0, 0, 0.1)',
                               '& .MuiOutlinedInput-notchedOutline': {
                                 border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                               },
                               '&:hover': {
                                 backgroundColor: theme.palette.mode === 'dark'
                                   ? 'rgba(255, 255, 255, 0.2)'
                                   : 'rgba(240, 240, 240, 1)',
                                 boxShadow: theme.palette.mode === 'dark'
                                   ? '0 2px 12px rgba(0, 0, 0, 0.4)'
                                   : '0 2px 12px rgba(0, 0, 0, 0.15)',
                               },
                               '&.Mui-focused': {
                                 backgroundColor: theme.palette.mode === 'dark'
                                   ? 'rgba(255, 255, 255, 0.25)'
                                   : 'rgba(255, 255, 255, 1)',
                                 boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                                 '& .MuiOutlinedInput-notchedOutline': {
                                   border: `2px solid ${theme.palette.primary.main}`,
                                 },
                               },
                               paddingLeft: 1.5,
                               height: '50px',
                               transition: 'all 0.3s ease',
                             },
                           }}
                           sx={{
                             width: 350,
                             maxWidth: '100%',
                             '& .MuiInputBase-input': {
                               padding: '10px 14px',
                               color: theme.palette.text.primary,
                               fontWeight: 500,
                             },
                           }}
                         />
        <Button
          variant="contained"
          onClick={handleAddIdCard}
          sx={{
            ml: 2,
            width: '160px',
            height: '50px',
            borderRadius: '10px',
            padding: '10px 0',
            fontWeight: 'bold',
            backgroundColor: theme.palette.primary.main,
            '&:hover': { backgroundColor: theme.palette.primary.dark },
          }}
          startIcon={<AddIcon />}
        >
          ADD ID CARD
        </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0, // Remove MUI's default padding
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      </Box>
      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table
              sx={{
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white'
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 80, whiteSpace: 'nowrap' }}>S.No.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 180, whiteSpace: 'nowrap' }}>Student Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 150, whiteSpace: 'nowrap' }}>Issue Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', minWidth: 150, whiteSpace: 'nowrap' }}>Expiry Date</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1.1rem', minWidth: 120, whiteSpace: 'nowrap' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((idCard, index) => (
                    <TableRow
                      key={idCard.id}
                      sx={{
                        backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{idCard.studentName}</TableCell>
                      <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{idCard.issueDate}</TableCell>
                      <TableCell sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{idCard.expiryDate}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleEdit(idCard)}
                          sx={{
                            color: theme.palette.primary.light,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(idCard)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{
                    backgroundColor: theme.palette.background.paper,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}>
                    <TableCell colSpan={5} align="center" sx={{ fontSize: '1rem', padding: '20px', wordSpacing: 7 }}>
                      No Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>Rows</InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Add ID Card Modal */}
      <AddIdCardModal
        open={showAddIdCardModal}
        onClose={() => setShowAddIdCardModal(false)}
        onSuccess={handleSaveIdCard}
      />

      {/* Edit ID Card Modal */}
      <EditIdCardModal
        open={showEditIdCardModal}
        onClose={() => setShowEditIdCardModal(false)}
        onSuccess={handleUpdateIdCard}
        idCard={selectedIdCard}
      />

      {/* Delete Modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete the ID Card for <strong>{selectedIdCard?.studentName}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmDelete}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              {successModalType === 'add' ? 'ID Card Added ✅' : 'ID Card Updated ✅'}
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              {successModalType === 'add' ? 'ID Card Added Successfully' : 'ID Card Updated Successfully'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => setShowSuccessModal(false)}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Modal
      open={showUserGuideModal}
      onClose={() => setShowUserGuideModal(false)}
      BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 800,
          bgcolor: 'background.paper',
          borderRadius: '16px',
          boxShadow: 24,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
            ID Card Management -For User Guide
          </Typography>
          <IconButton
            onClick={() => setShowUserGuideModal(false)}
            sx={{ color: '#fff', position: 'absolute', right: 14 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
   <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
  {/* Section 1: Add ID Card */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
  >
    ➤ Follow these steps to add a new ID card:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Click the <strong>"ADD ID CARD"</strong> button at the top-right corner to open the Add ID Card modal.
    </li>
    <li>
      Select a <strong>Candidate Name</strong> from the dropdown menu.
    </li>
    <li>
      Select an <strong>Issue Date</strong> using the date picker (this defaults to today’s date).
    </li>
    <li>
      Select an <strong>Expiry Date</strong> using the date picker.
    </li>
    <li>
      Before clicking the <strong>"SAVE"</strong> button, carefully review all entered details for accuracy.
    </li>
    <li>
      Click the <strong>"SAVE"</strong> button to add the ID card. A success message will confirm the action.
    </li>
    <li>
      The newly added ID card will then appear in the <strong>ID Card List</strong> table.
    </li>
    <li>
      To clear the form and close the modal, click the <strong>"RESET"</strong> button.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Candidate Name, Issue Date, and Expiry Date are mandatory fields.
  </Typography>

  {/* Section 2: Edit ID Card */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to Update an existing ID card:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Select the ID card record you want to edit or update from the <strong>ID Card List</strong> table.
    </li>
    <li>
      Click the <strong>Edit</strong> icon (pencil) in the Action column to open the Edit ID Card modal.
    </li>
    <li>
      Update the <strong>Candidate Name</strong>, <strong>Issue Date</strong>, or <strong>Expiry Date</strong> as required.
    </li>
    <li>
      Ensure all required fields are properly filled.
    </li>
    <li>
      Click the <strong>"UPDATE"</strong> button to save the changes. A success message will confirm the update.
    </li>
    <li>
      To discard changes and close the modal, click the <strong>"CANCEL"</strong> button.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Candidate Name, Issue Date, and Expiry Date are mandatory fields.
  </Typography>

  {/* Section 3: Delete ID Card */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to delete an ID card:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Select the ID card record you want to delete from the <strong>ID Card List</strong> table.
    </li>
    <li>
      Click the <strong>Delete</strong> icon (trash) in the Action column.
    </li>
    <li>
      Confirm the deletion by clicking <strong>"Yes, Delete"</strong> in the confirmation modal.
    </li>
    <li>
      The ID card record will be removed from the list, and a success message will be displayed.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Deleted ID cards cannot be recovered. Please proceed with caution.
  </Typography>

  {/* Section 4: Search ID Cards */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to search for an ID card:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Use the search bar located at the top-left of the <strong>ID Card List</strong>.
    </li>
    <li>
      Enter the <strong>Candidate Name</strong>, <strong>Issue Date</strong>, or <strong>Expiry Date</strong> (or part of them) to filter the results.
    </li>
    <li>
      The table will automatically update to display matching ID cards.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: The search is case-insensitive and filters by Candidate Name, Issue Date, and Expiry Date.
  </Typography>

  {/* Section 5: Pagination */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to navigate the ID card list:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Use the pagination controls at the bottom of the table to browse through ID card records.
    </li>
    <li>
      Click the left (<strong>&lt;</strong>) or right (<strong>&gt;</strong>) arrows to move between pages.
    </li>
    <li>
      Click on a specific page number to navigate directly to that page.
    </li>
    <li>
      Adjust the number of rows displayed per page using the dropdown menu (for example, 5, 10, 20, or 30 rows).
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: The table displays only the selected number of rows per page.
  </Typography>
</CustomTableScrollbar>

      </Box>
    </Modal>
    </Container>
  );
};

// AddIdCardModal Component
const AddIdCardModal = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const [candidateId, setCandidateId] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');

  const staticCandidateData = [
    { candidateId: 1, candidateName: 'John Doe' },
    { candidateId: 2, candidateName: 'Jane Smith' },
    { candidateId: 3, candidateName: 'Alice Johnson' },
    { candidateId: 4, candidateName: 'Bob Brown' },
    { candidateId: 5, candidateName: 'Charlie Davis' },
  ];

  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    setIssueDate(formattedDate);
  }, []);

  const handleSave = (e) => {
    e.preventDefault();
    if (candidateId && issueDate && expiryDate) {
      onSuccess();
      handleCancel();
    } else {
      alert('Please fill all fields');
    }
  };

  const handleCancel = () => {
    setCandidateId('');
    setIssueDate('');
    setExpiryDate('');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 700,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
            Add ID Card
          </Typography>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ padding: '20px', overflowY: 'auto', flexGrow: 1 }}>
          <form onSubmit={handleSave}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="candidate-label" sx={{ color: theme.palette.text.primary }}>Candidate Name</InputLabel>
              <Select
                labelId="candidate-label"
                id="candidate"
                value={candidateId}
                label="Candidate Name"
                onChange={(e) => setCandidateId(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              >
                {staticCandidateData.map((candidate) => (
                  <MenuItem key={candidate.candidateId} value={candidate.candidateId}>
                    {candidate.candidateName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Issue Date"
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                },
              }}
            />
            <TextField
              label="Expiry Date"
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip
                title={
                  !candidateId ? "Select Candidate" :
                    !issueDate ? "Select Issue Date" :
                      !expiryDate ? "Select Expiry Date" : ""
                }
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{
                      mr: 2,
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                    }}
                    disabled={!candidateId || !issueDate || !expiryDate}
                  >
                    SAVE
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancel}
              >
                RESET
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Modal>
  );
};

// EditIdCardModal Component
const EditIdCardModal = ({ open, onClose, onSuccess, idCard }) => {
  const theme = useTheme();
  const [candidateId, setCandidateId] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');

  const staticCandidateData = [
    { candidateId: 1, candidateName: 'John Doe' },
    { candidateId: 2, candidateName: 'Jane Smith' },
    { candidateId: 3, candidateName: 'Alice Johnson' },
    { candidateId: 4, candidateName: 'Bob Brown' },
    { candidateId: 5, candidateName: 'Charlie Davis' },
  ];

  useEffect(() => {
    if (idCard) {
      setCandidateId(staticCandidateData.find(candidate => candidate.candidateName === idCard.studentName)?.candidateId || '');
      setIssueDate(idCard.issueDate);
      setExpiryDate(idCard.expiryDate);
    }
  }, [idCard]);

  const handleSave = (e) => {
    e.preventDefault();
    if (candidateId && issueDate && expiryDate) {
      onSuccess();
      onClose();
    } else {
      alert('Please fill all fields');
    }
  };

  const handleCancel = () => {
    if (idCard) {
      setCandidateId(staticCandidateData.find(candidate => candidate.candidateName === idCard.studentName)?.candidateId || '');
      setIssueDate(idCard.issueDate);
      setExpiryDate(idCard.expiryDate);
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 700,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
            Edit ID Card
          </Typography>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ padding: '20px', overflowY: 'auto', flexGrow: 1 }}>
          <form onSubmit={handleSave}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="candidate-label" sx={{ color: theme.palette.text.primary }}>Candidate Name</InputLabel>
              <Select
                labelId="candidate-label"
                id="candidate"
                value={candidateId}
                label="Candidate Name"
                onChange={(e) => setCandidateId(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              >
                {staticCandidateData.map((candidate) => (
                  <MenuItem key={candidate.candidateId} value={candidate.candidateId}>
                    {candidate.candidateName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Issue Date"
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                },
              }}
            />
            <TextField
              label="Expiry Date"
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip
                title={
                  !candidateId ? "Select Candidate" :
                    !issueDate ? "Select Issue Date" :
                      !expiryDate ? "Select Expiry Date" : ""
                }
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{
                      mr: 2,
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                    }}
                    disabled={!candidateId || !issueDate || !expiryDate}
                  >
                    UPDATE
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancel}
              >
                CANCEL
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Modal>
  );
};

export default IdCard;
